import React from "react";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  InputGroup,
  InputProps,
  InputRightElement,
} from "@chakra-ui/react";
import { useController, Control, FieldValues, Path } from "react-hook-form";

interface FormPercentageInputProps<T extends FieldValues>
  extends Omit<InputProps, "name" | "value" | "onChange"> {
  name: Path<T>;
  label: string;
  control: Control<T, any, any>;
  isRequired?: boolean;
}

export function FormPercentageInput<T extends FieldValues>({
  name,
  label,
  control,
  isRequired = false,
  ...inputProps
}: FormPercentageInputProps<T>) {
  const {
    field: { value, onChange, ...field },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value.replace(/\D/g, ""); // Remove non-numeric characters
    // Always pad with zeros to ensure at least 3 digits
    const padded = inputValue.padStart(3, "0");
    const integerPart = padded.slice(0, -2);
    const decimalPart = padded.slice(-2);
    console.log("Formatted Value:", `${integerPart}.${decimalPart}`);
    onChange(`${integerPart}.${decimalPart}`);
  };

  const displayValue = (value: string | number) => {
    const castedValue = typeof value === "number" ? `${value}` : value;
    // Remove leading zeros from integer part
    const [integer = "0", decimal = "00"] = castedValue.split(".");
    const cleanInteger = integer.replace(/^0+/, "") || "0";
    const cleanDecimal = (decimal + "00").slice(0, 2);
    const formattedValue = `${cleanInteger},${cleanDecimal}`;
    if (formattedValue.length >= 6) {
      return "100,00";
    }
    return formattedValue;
  };

  return (
    <FormControl isInvalid={!!error} isRequired={isRequired}>
      <FormLabel>{label}</FormLabel>
      <InputGroup>
        <Input
          onFocus={(e) => e.target.select()}
          {...field}
          {...inputProps}
          value={displayValue(value)}
          onChange={handleChange}
          placeholder="0,00"
          textAlign={"right"}
        />
        <InputRightElement pointerEvents="none" color="gray.500" fontSize="sm">
          %
        </InputRightElement>
      </InputGroup>
      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </FormControl>
  );
}
